import {
  CheckCircle,
  Download,
  Printer,
  RefreshCw,
  Trash2,
  ClipboardList,
  FileText,
  Package,
  Hourglass,
  Ship,
  Anchor,
  Ruler,
  Tag,
  Scale,
  Triangle,
  Users,
  Tags
} from 'lucide-react'
import React, { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  useMasterBillActions,
  useMasterBillState
} from '../stores/useMasterBillStore'
import { useSearchActions, useSearchState } from '../stores/useSearchStore'

import MasterBillStatusBadge from './bill/MasterBillStatusBadge'
import MasterBillDate from './MasterBillDate'

import ConfirmDialog from '@/components/common/ConfirmDialog'
import { customsV2Service } from '@/services'
import { MasterBillStatus } from '@/services/CustomsV2Service'
import { useUIStore } from '@/stores/useUIStore'
import { HttpError } from '@/types/errors'

const MasterBillInfo: React.FC = () => {
  const { masterBill, houseBills, totalMarks, removeDialog, loading } =
    useMasterBillState()
  const {
    showPrintDialog,
    removeMasterBill,
    showRemoveDialog,
    closeRemoveDialog,
    fetchMasterBill
  } = useMasterBillActions()
  const { masterBillId } = useSearchState()
  const { fetchMasterBills, clearFilter } = useSearchActions()
  const { showError, showSuccess } = useUIStore()
  const [searchParams, setSearchParams] = useSearchParams()

  // 定义信息字段配置
  const infoFields = useMemo(
    () => [
      {
        icon: Package,
        label: '集装箱号',
        value: masterBill?.containerNumber || '-'
      },
      {
        icon: Ship,
        label: '船名',
        value: masterBill?.vesselName || '-'
      },
      {
        icon: Anchor,
        label: '航次',
        value: masterBill?.voyageNumber || '-'
      },
      {
        icon: Ruler,
        label: '规格',
        value: `${masterBill?.containerSize || ''}${
          masterBill?.containerType || ''
        }`
      },
      { icon: Users, label: '子提单数', value: `${houseBills.length} ` },
      { icon: Tags, label: '总唛头数', value: `${totalMarks} 件` },
      {
        icon: Package,
        label: '总货数',
        value: `${masterBill?.totalPackages || '-'} 箱`
      },
      {
        icon: Scale,
        label: '总重量',
        value: `${masterBill?.totalWeight || '-'} KG`
      },
      {
        icon: Triangle,
        label: '总体积',
        value: `${masterBill?.totalVolume || '-'} CBM`
      }
    ],
    [masterBill, totalMarks]
  )

  const removeMasterBillParam = () => {
    searchParams.delete('masterBill')
    setSearchParams(searchParams)
  }

  const confirmDeleteBillHandle = async () => {
    if (!masterBillId) {
      return
    }
    const { success, message } = await removeMasterBill(masterBillId)
    closeRemoveDialog()
    if (success) {
      fetchMasterBills()
      clearFilter()
      removeMasterBillParam()
      showSuccess(message)
    } else {
      showError(message)
    }
  }

  const handleConfirmData = async () => {
    if (!masterBillId) {
      showError('请先选择主提单')
      return
    }

    try {
      const result = await customsV2Service.updateMasterBillStatus(
        masterBillId,
        MasterBillStatus.AWAITING_DEVAN
      )
      if (result.success) {
        const { message } = result
        showSuccess(message)
        fetchMasterBill(masterBillId)
      }
    } catch {
      showError('资料确认失败')
    }
  }

  // 验证 blob 是否为有效的 Excel 文件
  const validateExcelBlob = async (blob: Blob) => {
    const isValidType =
      blob.type.includes('sheet') ||
      blob.type.includes('excel') ||
      blob.type === 'application/octet-stream'

    if (!isValidType && blob.size < 1000) {
      const text = await blob.text()
      if (text.includes('<!DOCTYPE html>') || text.includes('<html>')) {
        throw new Error('收到的是HTML响应而不是Excel文件')
      }
      if (text.includes('error') || text.includes('Error')) {
        throw new Error(`服务器返回错误: ${text}`)
      }
    }

    if (blob.size < 100) {
      throw new Error(`文件大小异常: ${blob.size} bytes`)
    }
  }

  // 下载 blob 文件
  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
  }

  const exportExcelFile = async () => {
    if (!masterBillId) {
      showError('请先选择主提单')
      return
    }

    try {
      const blob = await customsV2Service.exportExcel(masterBillId)
      await validateExcelBlob(blob)

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:-]/g, '')
        .replace('T', '_')
      const filename = `${
        masterBill?.masterBlNumber || 'unknown'
      }_${timestamp}.xlsx`

      downloadBlob(blob, filename)
      showSuccess('货物模板导出成功')
    } catch (error: unknown) {
      if (error instanceof Error) {
        showError(
          (error as HttpError).response?.data?.detail ||
            error.message ||
            '导出货物模板失败'
        )
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-gray-500">
          <RefreshCw className="h-6 w-6 animate-spin" />
          <span>加载主提单数据中...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <ClipboardList className="w-5 h-5" />
          主提单信息
        </h3>
        <span className='flex gap-2 flex-row-reverse '><MasterBillStatusBadge /> {masterBill?.masterBlNumber}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 其他字段使用配置映射 */}
        {infoFields.map((field, index) => (
          <div key={index}>
            <label className="flex items-center gap-1 text-sm font-medium text-gray-500">
              <field.icon className="w-4 h-4" />
              {field.label}
            </label>
            <p
              className={`mt-1 text-sm text-gray-900`}
            >
              {field.value}
            </p>
          </div>
        ))}
      </div>

      {/* 功能操作区 */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <span />
          <div className="flex space-x-3">
            {masterBill?.status === MasterBillStatus.PENDING_CONFIRMATION && (
              <button
                onClick={handleConfirmData}
                disabled={!masterBillId || !masterBill.devanDate}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                资料确认
              </button>
            )}
            <button
              onClick={showPrintDialog}
              disabled={!masterBillId}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <Printer className="w-4 h-4 mr-2" />
              打印预览
            </button>
            <button
              onClick={exportExcelFile}
              disabled={!masterBillId}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              导出模板
            </button>
            {masterBill?.status === MasterBillStatus.PENDING_CONFIRMATION && (
              <button
                onClick={showRemoveDialog}
                disabled={!masterBillId}
                className="inline-flex items-center px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                删除
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 日期管理面板组件 */}
      <MasterBillDate />

      {/* 删除确认对话框 */}
      <ConfirmDialog
        isOpen={removeDialog}
        title="删除主提单"
        message={`确定要删除主提单 "${masterBill?.masterBlNumber}" 吗？此操作将删除该主提单及其所有相关数据（包括分提单、分货详情、入库扫描记录等），且不可恢复。`}
        confirmText="删除"
        cancelText="取消"
        onConfirm={confirmDeleteBillHandle}
        onCancel={closeRemoveDialog}
        isLoading={loading}
        isDangerous={true}
      />
    </div>
  )
}

export default MasterBillInfo
